/**
 * IMS-2 — 將公開行程分享到單一既有 IM 對話。
 * 僅送 conversationId／clientMessageId／附言；快照與授權一律由後端決定。
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { conversationApi, memberSummaryApi } from '@formosa/shared/api/im';
import { publicJourneyApi } from '../../api/publicJourneyApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { Alert, Button } from '../../components/ui';
import {
  conversationTitle,
  createClientMessageId,
  indexSummaries,
  summaryIdsFromConversations,
} from '../messages/travelChatModel';
import {
  SHARE_MESSAGE_MAX,
  shareErrorMessage,
  shareResultConversationId,
  shareableConversations,
} from './journeyImShareModel';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function listFocusable(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((node) => node.getAttribute('aria-hidden') !== 'true');
}

export default function ShareJourneyToImSheet({ publicationId, journeyTitle, onClose }) {
  const { t } = useTravelTranslation();
  const titleId = useId();
  const noteId = useId();
  const radioName = useId();
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const generation = useRef(0);
  const mountedRef = useRef(true);
  /** 同一次分享意圖共用；成功、改選對話或改附言後才重新產生。 */
  const clientMessageIdRef = useRef('');
  const inFlightRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [phase, setPhase] = useState('loading');
  const [conversations, setConversations] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [selectedId, setSelectedId] = useState('');
  const [note, setNote] = useState('');
  const [submitPhase, setSubmitPhase] = useState('idle');
  const [submitError, setSubmitError] = useState('');
  const [sharedConversationId, setSharedConversationId] = useState('');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generation.current += 1;
    };
  }, []);

  const loadConversations = useCallback(async () => {
    const token = ++generation.current;
    setPhase('loading');
    setSelectedId('');
    try {
      const list = shareableConversations(await conversationApi.listMine());
      if (token !== generation.current || !mountedRef.current) return;
      setConversations(list);
      setPhase('ready');
      const ids = summaryIdsFromConversations(list.filter((row) => row.conversationType === 'DIRECT'));
      if (!ids.length) return;
      try {
        const rows = await memberSummaryApi.batch(ids);
        if (token !== generation.current || !mountedRef.current) return;
        setSummaries(indexSummaries(rows));
      } catch {
        /* 名稱退回通用標題 */
      }
    } catch {
      if (token !== generation.current || !mountedRef.current) return;
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        if (inFlightRef.current) return;
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const root = panelRef.current;
      const nodes = listFocusable(root);
      if (!nodes.length) {
        event.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first || !root?.contains(document.activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last || !root?.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, []);

  function requestClose() {
    if (inFlightRef.current) return;
    onCloseRef.current?.();
  }

  function startNewIntent() {
    if (inFlightRef.current) return;
    clientMessageIdRef.current = '';
    setSubmitError('');
    setSubmitPhase('idle');
  }

  function selectConversation(conversationId) {
    if (inFlightRef.current || conversationId === selectedId) return;
    startNewIntent();
    setSelectedId(conversationId);
  }

  function changeNote(value) {
    if (inFlightRef.current) return;
    startNewIntent();
    setNote(value.slice(0, SHARE_MESSAGE_MAX));
  }

  async function submit() {
    if (inFlightRef.current || !selectedId) return;
    if (!conversations.some((row) => row.conversationId === selectedId)) return;
    if (!clientMessageIdRef.current) clientMessageIdRef.current = createClientMessageId();
    const clientMessageId = clientMessageIdRef.current;
    const conversationId = selectedId;
    inFlightRef.current = true;
    setSubmitPhase('submitting');
    setSubmitError('');
    try {
      const result = await publicJourneyApi.shareToIm(publicationId, {
        conversationId,
        clientMessageId,
        message: note,
      });
      if (!mountedRef.current) return;
      clientMessageIdRef.current = '';
      setSharedConversationId(shareResultConversationId(result, conversationId));
      setSubmitPhase('success');
    } catch (cause) {
      if (!mountedRef.current) return;
      setSubmitError(shareErrorMessage(cause, t));
      setSubmitPhase('error');
    } finally {
      inFlightRef.current = false;
    }
  }

  const submitting = submitPhase === 'submitting';

  return (
    <div
      className="journey-share-sheet__scrim"
      data-testid="journey-share-sheet-scrim"
      role="presentation"
      onClick={requestClose}
    >
      <div
        ref={panelRef}
        className="journey-share-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="journey-share-sheet"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="journey-share-sheet__header">
          <h2 id={titleId}>分享至聊天室</h2>
          <button
            ref={closeRef}
            type="button"
            className="journey-share-sheet__close"
            disabled={submitting}
            onClick={requestClose}
          >
            取消
          </button>
        </div>
        {journeyTitle ? <p className="journey-share-sheet__subject">{journeyTitle}</p> : null}

        {submitPhase === 'success' ? (
          <div className="journey-share-sheet__state" data-testid="journey-share-success">
            <Alert variant="success">已分享到聊天室。</Alert>
            <div className="journey-share-sheet__actions">
              <Button type="button" variant="secondary" onClick={requestClose}>返回行程</Button>
              <Link className="journey-share-sheet__open" to={`/messages/${sharedConversationId}`}>開啟聊天</Link>
            </div>
          </div>
        ) : null}

        {submitPhase !== 'success' && phase === 'loading' ? (
          <div className="journey-share-sheet__state" aria-live="polite" aria-busy="true" data-testid="journey-share-loading">
            <p>正在載入聊天室…</p>
          </div>
        ) : null}

        {submitPhase !== 'success' && phase === 'error' ? (
          <div className="journey-share-sheet__state" data-testid="journey-share-load-error">
            <Alert variant="error">無法載入聊天室，請稍後再試。</Alert>
            <Button type="button" onClick={loadConversations}>重新載入</Button>
          </div>
        ) : null}

        {submitPhase !== 'success' && phase === 'ready' && conversations.length === 0 ? (
          <div className="journey-share-sheet__state" data-testid="journey-share-empty">
            <p>目前沒有可以分享的聊天室。</p>
            <p>先和旅伴開始對話，再回來分享這個行程。</p>
          </div>
        ) : null}

        {submitPhase !== 'success' && phase === 'ready' && conversations.length > 0 ? (
          <form
            className="journey-share-sheet__form"
            onSubmit={(event) => { event.preventDefault(); submit(); }}
          >
            <fieldset className="journey-share-sheet__list" disabled={submitting}>
              <legend>選擇一個聊天室</legend>
              {conversations.map((conversation) => {
                const selected = selectedId === conversation.conversationId;
                return (
                  <label
                    key={conversation.conversationId}
                    className={`journey-share-sheet__option${selected ? ' is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={radioName}
                      value={conversation.conversationId}
                      checked={selected}
                      onChange={() => selectConversation(conversation.conversationId)}
                    />
                    <span className="journey-share-sheet__option-title">
                      {conversationTitle(conversation, summaries)}
                    </span>
                    <span className="journey-share-sheet__option-meta">
                      {conversation.conversationType === 'GROUP' ? '群組' : '私訊'}
                    </span>
                  </label>
                );
              })}
            </fieldset>
            <label className="journey-share-sheet__note-label" htmlFor={noteId}>附上一句話（選填）</label>
            <textarea
              id={noteId}
              className="journey-share-sheet__note"
              rows={2}
              maxLength={SHARE_MESSAGE_MAX}
              value={note}
              disabled={submitting}
              onChange={(event) => changeNote(event.target.value)}
            />
            {submitError ? <Alert variant="error">{submitError}</Alert> : null}
            <div className="journey-share-sheet__footer">
              <Button
                type="submit"
                loading={submitting}
                disabled={!selectedId}
                data-testid="journey-share-confirm"
              >
                {submitting ? '分享中…' : submitPhase === 'error' ? '再試一次' : '分享'}
              </Button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}
